/**
 * Durations cross the contract in seconds and are edited in minutes.
 *
 * The conversion lives here rather than in each screen because a stray `/ 60`
 * in a form handler is the kind of thing that silently turns a sixty-minute
 * budget into a one-minute one.
 */

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

export const toMinutes = (seconds: number): number => Math.round(seconds / SECONDS_PER_MINUTE);

export const toSeconds = (minutes: number): number => Math.round(minutes * SECONDS_PER_MINUTE);

/**
 * Splits a duration for display.
 *
 * Returns the parts rather than a formatted string: "1 sa 30 dk" and
 * "1 hr 30 min" are different sentences in different languages, so the words
 * belong to a translation key and only the numbers belong here.
 */
/**
 * A running time, as `3:41`.
 *
 * Floors rather than rounds, which `toMinutes` deliberately does not: a 221
 * second video is three minutes and forty-one seconds, and rounding the minutes
 * while printing the true seconds produces `4:41` — a number that is wrong in
 * both halves at once.
 */
export const formatClock = (seconds: number): string => {
  const total = Math.max(Math.round(seconds), 0);
  return `${Math.floor(total / SECONDS_PER_MINUTE)}:${String(total % SECONDS_PER_MINUTE).padStart(2, '0')}`;
};

export const splitDuration = (seconds: number): { hours: number; minutes: number } => {
  const totalMinutes = toMinutes(Math.max(seconds, 0));
  return {
    hours: Math.floor(totalMinutes / MINUTES_PER_HOUR),
    minutes: totalMinutes % MINUTES_PER_HOUR,
  };
};
